import { fdmcaPart1DataSchema } from "../../schemas/mhaForms/mca/fdmca.js";
import { fdmcaAvtSections } from "./fdmcaAvtSections.js";
import { responsibleDecisionMakerFields } from "./responsibleDecisionMakerFields.js";
export const fdmcaPart1Template = {
    signing: {
        userOrTeamRequirement: {
            accreditation: ["doctor", "nurse", "hcpc", "social-worker"],
            teamType: [],
        },
    },
    formBuilderFields: [
        {
            type: "readonly",
            label: "Name and address of person",
            textFn: (data) => [
                data.patient?.name || "",
                [data.patient?.address, data.patient?.postalCode]
                    .filter(Boolean)
                    .join(", "),
            ],
            isReadOnlyPatientInfo: true,
        },
        {
            type: "practitioner",
            label: "Name and address of person completing this form",
            field: "user",
        },
        {
            type: "label",
            bannerType: "info",
            label: [
                "This form may be used where:\n• the decision concerns care or treatment for a physical disorder, or for a mental disorder where Part 4 of the Mental Health Act does not apply\n• you are establishing capacity and consent for the purposes of sections 58 and 58A of the Mental Health Act\nDo not use this form as an alternative to the Part 4 treatment provisions. Where Part 4 applies, treatment for mental disorder is authorised under the Mental Health Act.",
            ],
        },
        {
            type: "textbox",
            field: "decisionOutline",
            label: "Outline the decision to be made",
            rows: "5",
        },
        {
            type: "textbox",
            field: "supportSteps",
            label: "Describe the steps taken to support the person to make the decision, including any reasonable adjustments.",
            rows: "5",
        },
        {
            type: "textbox",
            field: "supportingPeople",
            label: "List the people who supported the person while their capacity was being assessed (e.g. family carer, IMCA, learning disability liaison nurse).",
            rows: "5",
        },
        {
            type: "branch",
            field: "materialTime",
            label: "What is the 'material time' for this capacity assessment?",
            tooltip: "Material time is the legal term for the period this capacity assessment covers. It may be a single, one-off decision, or decisions that are repeated or continue over time, such as ongoing care arrangements.",
            branches: [
                {
                    fieldValue: "one_off",
                    label: "One-off decision",
                    fields: [],
                    fieldsOwned: [],
                },
                {
                    fieldValue: "repeated_or_continual",
                    label: "Repeated or continual decisions",
                    fields: [],
                    fieldsOwned: [],
                },
            ],
        },
        {
            type: "textbox",
            field: "additionalDecisionInformation",
            label: "Record any information relevant to the decision being made",
            rows: "5",
        },
        {
            type: "label",
            labelType: "subtitle",
            label: ["Functional Test (Ability to make a decision)"],
        },
        {
            type: "label",
            bannerType: "info",
            label: [
                "The questions below should be answered by reference to the decision, the relevant information, the material time, and the support described above.",
            ],
        },
        {
            type: "branch",
            field: "canUnderstandInformation",
            label: "Is the person unable to understand the relevant information needed to make the decision?",
            tooltip: "Consider whether you have provided the information in a way the person can understand. Consider whether the person understands the consequences of deciding one way or another, or of not making a decision.",
            branches: [
                { fieldValue: "able", label: "Able", fields: [], fieldsOwned: [] },
                { fieldValue: "unable", label: "Unable", fields: [], fieldsOwned: [] },
            ],
        },
        {
            type: "textbox",
            field: "reasoningUnderstand",
            label: "Record reasoning",
            rows: "5",
        },
        {
            type: "branch",
            field: "canRetainInformation",
            label: "Is the person unable to retain the relevant information long enough to make the decision?",
            tooltip: "Consider whether the person can remember the information long enough to make the decision.\nRetention for a short period does not prevent the ability to make a decision.",
            branches: [
                { fieldValue: "able", label: "Able", fields: [], fieldsOwned: [] },
                { fieldValue: "unable", label: "Unable", fields: [], fieldsOwned: [] },
            ],
        },
        {
            type: "textbox",
            field: "reasoningRetain",
            label: "Record reasoning",
            rows: "5",
        },
        {
            type: "branch",
            field: "canUseAndWeighInformation",
            label: "Is the person unable to use or weigh the relevant information as part of making the decision?",
            branches: [
                { fieldValue: "able", label: "Able", fields: [], fieldsOwned: [] },
                { fieldValue: "unable", label: "Unable", fields: [], fieldsOwned: [] },
            ],
        },
        {
            type: "textbox",
            field: "reasoningUseAndWeigh",
            label: "Record reasoning",
            rows: "5",
        },
        {
            type: "branch",
            field: "canCommunicateDecision",
            label: "Is the person unable to communicate their decision, for example, simple language, visual aids or any other means?",
            branches: [
                { fieldValue: "able", label: "Able", fields: [], fieldsOwned: [] },
                { fieldValue: "unable", label: "Unable", fields: [], fieldsOwned: [] },
            ],
        },
        {
            type: "textbox",
            field: "reasoningCommunicate",
            label: "Record reasoning",
            rows: "5",
        },
        {
            type: "label",
            labelType: "subtitle",
            label: ["Diagnostic Test (Mental impairment)"],
        },
        {
            type: "branch",
            field: "impairmentDisturbance",
            label: "Do you consider that the person is unable to either understand, retain, use and weigh or communicate their decision because of an impairment or disturbance in the functioning of the mind or brain?",
            tooltip: "Review your answers above. If you have answered 'Unable' to any of the questions, you need to assess whether this is because of an impairment or disturbance in how the person's mind or brain is working. It doesn't matter if the impairment or disturbance is temporary or permanent. For example, is there a reasonable suggestion that this person might have dementia, delirium, a mental illness, stress or severe emotional difficulties?",
            branches: [
                {
                    fieldValue: "yes",
                    label: "Yes",
                    fieldsOwned: ["impairmentType"],
                    fields: [
                        {
                            type: "textbox",
                            field: "impairmentType",
                            label: "What impairment do they appear to have?",
                            rows: "5",
                        },
                    ],
                },
                {
                    fieldValue: "no",
                    label: "No",
                    fieldsOwned: [],
                    fields: [
                        {
                            type: "label",
                            bannerType: "warning",
                            label: [
                                "A person who is able to understand, retain, use and weigh the relevant information, and communicate their decision, has capacity to make this decision.",
                            ],
                        },
                    ],
                },
            ],
        },
        {
            type: "label",
            labelType: "subtitle",
            label: ["Conclusion"],
        },
        {
            type: "branch",
            field: "lackOfCapacity",
            label: "Based on your answers above, have you determined a lack of capacity to make the decision at this time?",
            tooltip: "A person is not to be treated as unable to make a decision merely because they make an unwise decision.",
            branches: [
                {
                    fieldValue: "has-capacity",
                    label: "The person is able to make this decision",
                    fields: [],
                    fieldsOwned: [],
                },
                {
                    fieldValue: "lacks-capacity",
                    label: "The person lacks capacity to make this decision",
                    fields: [],
                    fieldsOwned: [],
                },
            ],
        },
        {
            type: "textbox",
            field: "reasoningLackOfCapacity",
            label: "Record reasoning",
            rows: "5",
        },
        ...responsibleDecisionMakerFields(),
    ],
    avtSections: fdmcaAvtSections,
    dataSchema: fdmcaPart1DataSchema,
};
export const fdmcaV1Base = {
    id: "mca-fdmca",
    category: "mca",
    version: "1.0",
    status: "active",
    formName: "FDMCA",
    section: "",
    parts: [fdmcaPart1Template],
    description: "Functional and Diagnostic Mental Capacity Assessment",
    displayTitle: "Functional and Diagnostic Mental Capacity Assessment",
    extractGenericData: () => ({}),
};
//# sourceMappingURL=fdmca.js.map